import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bot, Eye, Loader2, X, ListChecks } from 'lucide-react';
import { vendorContractApi, applyContractPlaceholders } from '../../services/vendorContractService';
import { vendorBookingApi } from '../../services/vendorBookingService';
import { useVendorProfile } from '../../services/vendorProfileService';
import { supportChatApi } from '../../services/supportChatService';
import { normalizeChatReply } from '../../utils/chatWidgetUtils';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import ModalShell from '../../components/shared/ModalShell';
import DatePicker from '../../components/client/DatePicker';
import BrandImagePicker from '../../components/vendor/branding/BrandImagePicker';
import ContractRichTextEditor from '../../components/vendor/contract/ContractRichTextEditor';
import ContractBodyHtml from '../../components/vendor/contract/ContractBodyHtml';
import SignaturePad from '../../components/vendor/contract/SignaturePad';
import { useAuth } from '../../contexts/AuthContext';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorContractCreate = () => {
 const navigate = useNavigate();
 const queryClient = useQueryClient();
 const { id: editId } = useParams();
 const [searchParams] = useSearchParams();
 const copyFromId = searchParams.get('from');
 const bookingIdParam = searchParams.get('booking_id');
 const guestNameParam = searchParams.get('guest_name') || '';
 const guestEmailParam = searchParams.get('guest_email') || '';
 const guestPhoneParam = searchParams.get('guest_phone') || '';
 const eventDateParam = searchParams.get('event_date') || '';
 const eventTypeParam = searchParams.get('event_type') || '';
 const isEdit = Boolean(editId);
 const linkedBookingIdRef = useRef(null);
 const hydratedBookingRef = useRef(null);
 const hydratedGuestPrefillRef = useRef(false);
 const { success, error: showError } = useToastContext();
 const { user } = useAuth();
 const { data: profileResp } = useVendorProfile();
 const { isFreeTier, hasActiveSubscription } = useVendorPlanAccess();
 const canUseAi = hasActiveSubscription === true && !isFreeTier;
 const hydratedFromRef = useRef(null);
 const hydratedEditRef = useRef(null);

 const vp = profileResp?.data ?? {};
 const profileLogo = vp.logo_url || vp.brand_settings?.logo_url || null;
 const defaultVendorSigner =
 user?.name ||
 [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
 vp.business_name ||
 '';
 const vendorAddress = [
 vp.address,
 [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
 vp.country,
 ]
 .filter(Boolean)
 .join(' · ');

 const [templateId, setTemplateId] = useState('');
 const [title, setTitle] = useState('');
 const [clientName, setClientName] = useState('');
 const [clientEmail, setClientEmail] = useState('');
 const [clientPhone, setClientPhone] = useState('');
 const [serviceDescription, setServiceDescription] = useState('');
 const [eventDate, setEventDate] = useState('');
 const [amount, setAmount] = useState('');
 const [bodyHtml, setBodyHtml] = useState('');
 const [notes, setNotes] = useState('');
 const [logoUrl, setLogoUrl] = useState(null);
 const [logoInitialized, setLogoInitialized] = useState(false);
 const [aiPrompt, setAiPrompt] = useState('');
 const [isGenerating, setIsGenerating] = useState(false);
 const [aiError, setAiError] = useState('');
 const [previewOpen, setPreviewOpen] = useState(false);
 const [vendorSignerName, setVendorSignerName] = useState('');
 const [vendorSignatureData, setVendorSignatureData] = useState(null);
 const [existingVendorSignature, setExistingVendorSignature] = useState(null);
 const [clearVendorSignature, setClearVendorSignature] = useState(false);
 const [vendorSignPadKey, setVendorSignPadKey] = useState(0);
 const [linkedClientId, setLinkedClientId] = useState(null);

 const { data: templatesRes, isLoading: templatesLoading } = useQuery({
 queryKey: ['contract-templates'],
 queryFn: () => vendorContractApi.templates(),
 });
 const templates = useMemo(() => templatesRes?.data || [], [templatesRes?.data]);

 const { data: copyFromRes, isLoading: copyLoading } = useQuery({
 queryKey: ['vendor-contract', copyFromId],
 queryFn: () => vendorContractApi.getOne(copyFromId),
 enabled: Boolean(copyFromId) && !isEdit,
 });

 const { data: bookingPrefillRes, isLoading: bookingPrefillLoading } = useQuery({
 queryKey: ['vendorBooking', bookingIdParam],
 queryFn: () => vendorBookingApi.getVendorBookingById(bookingIdParam),
 enabled: Boolean(bookingIdParam) && !isEdit && !copyFromId,
 });

 const { data: editRes, isLoading: editLoading, error: editError } = useQuery({
 queryKey: ['vendor-contract', editId],
 queryFn: () => vendorContractApi.getOne(editId),
 enabled: isEdit,
 });

 // Business logo is the display default — don't copy it into logoUrl on create.
 useEffect(() => {
 if (isEdit || copyFromId || logoInitialized) return;
 if (profileResp) {
 setLogoInitialized(true);
 }
 }, [isEdit, copyFromId, profileResp, logoInitialized]);

 useEffect(() => {
 if (!copyFromId || !copyFromRes?.data || isEdit) return;
 if (hydratedFromRef.current === copyFromId) return;
 hydratedFromRef.current = copyFromId;
 const src = copyFromRes.data;
 setTitle(src.title ? `${src.title} (copy)` : 'Contract copy');
 setBodyHtml(src.body_html || '');
 setTemplateId(src.template_id || '');
 setLogoUrl(
 src.logo_url && src.logo_url !== profileLogo ? src.logo_url : null
 );
 setLogoInitialized(true);
 setNotes('');
 setClientName('');
 setClientEmail('');
 setClientPhone('');
 setVendorSignerName(defaultVendorSigner);
 setVendorSignatureData(null);
 setExistingVendorSignature(null);
 setClearVendorSignature(false);
 setVendorSignPadKey((k) => k + 1);
 success('Copied contract', 'Update the client details, then save as a new contract.');
 }, [copyFromId, copyFromRes, isEdit, profileLogo, success, defaultVendorSigner]);

 useEffect(() => {
 if (!isEdit || !editRes?.data) return;
 const src = editRes.data;
 if (hydratedEditRef.current === src.id) return;
 if (['signed', 'cancelled'].includes(String(src.status || '').toLowerCase())) {
 showError('This contract cannot be edited.');
 navigate(`/vendor/contracts/${editId}`, { replace: true });
 return;
 }
 hydratedEditRef.current = src.id;
 setTitle(src.title || '');
 setBodyHtml(src.body_html || '');
 setTemplateId(src.template_id || '');
 setClientName(src.client_name || '');
 setClientEmail(src.client_email || '');
 setClientPhone(src.client_phone || '');
 setNotes(src.notes || '');
 setLogoUrl(
 src.logo_url && src.logo_url !== profileLogo ? src.logo_url : null
 );
 setLogoInitialized(true);
 setVendorSignerName(src.vendor_signer_name || defaultVendorSigner);
 setExistingVendorSignature(src.vendor_signature_data || null);
 setVendorSignatureData(null);
 setClearVendorSignature(false);
 setVendorSignPadKey((k) => k + 1);
 setLinkedClientId(src.client_id || null);
 linkedBookingIdRef.current = src.booking_id || null;
 }, [isEdit, editRes, editId, navigate, showError, profileLogo, defaultVendorSigner]);

 // Prefill signer name on create once profile/user is known
 useEffect(() => {
 if (isEdit || copyFromId || vendorSignerName) return;
 if (defaultVendorSigner) setVendorSignerName(defaultVendorSigner);
 }, [isEdit, copyFromId, defaultVendorSigner, vendorSignerName]);

 // Prefill guest details from lead / Connected work query params
 useEffect(() => {
 if (isEdit || copyFromId || bookingIdParam || hydratedGuestPrefillRef.current) return;
 if (!guestNameParam && !guestEmailParam && !guestPhoneParam && !eventDateParam) return;
 hydratedGuestPrefillRef.current = true;
 if (guestNameParam) setClientName(guestNameParam);
 if (guestEmailParam) setClientEmail(guestEmailParam);
 if (guestPhoneParam) setClientPhone(guestPhoneParam);
 if (eventDateParam) setEventDate(String(eventDateParam).slice(0, 10));
 if (eventTypeParam) setServiceDescription(eventTypeParam);
 }, [
 isEdit,
 copyFromId,
 bookingIdParam,
 guestNameParam,
 guestEmailParam,
 guestPhoneParam,
 eventDateParam,
 eventTypeParam,
 ]);

 // Prefill client + event fields when creating from a booking
 useEffect(() => {
 if (isEdit || copyFromId || !bookingIdParam || !bookingPrefillRes?.data) return;
 if (hydratedBookingRef.current === bookingIdParam) return;
 hydratedBookingRef.current = bookingIdParam;
 linkedBookingIdRef.current = bookingIdParam;

 const booking = bookingPrefillRes.data;
 const client = booking.client || {};
 const displayName =
 booking.client_display_name ||
 booking.guest_name ||
 guestNameParam ||
 client.name ||
 '';
 const email = booking.guest_email || client.email || guestEmailParam || '';
 const phone = booking.guest_phone || client.phone || guestPhoneParam || '';
 const serviceName =
 booking.custom_service_name ||
 booking.vendor_service?.service_name ||
 '';
 const packageName =
 booking.custom_package_name ||
 booking.vendor_service_package?.package_name ||
 '';
 const serviceLabel = [serviceName, packageName].filter(Boolean).join(' — ') || eventTypeParam;
 const amountValue =
 booking.vendor_payout_amount ??
 booking.subtotal_amount ??
 booking.total_amount ??
 '';

 setClientName(displayName);
 setClientEmail(email);
 setClientPhone(phone);
 setLinkedClientId(booking.client_id || client.id || null);
 if (booking.event_date) {
 setEventDate(String(booking.event_date).slice(0, 10));
 } else if (eventDateParam) {
 setEventDate(String(eventDateParam).slice(0, 10));
 }
 if (serviceLabel) setServiceDescription(serviceLabel);
 if (amountValue !== '' && amountValue != null) {
 setAmount(String(Number(amountValue).toFixed(2)));
 }
 setTitle((prev) =>
 prev.trim()
 ? prev
 : booking.booking_reference
 ? `Service Agreement — ${booking.booking_reference}`
 : 'Service Agreement'
 );
 success('Booking linked', 'Client and event details were filled from this booking.');
 }, [isEdit, copyFromId, bookingIdParam, bookingPrefillRes, success]);

 const selectedTemplate = useMemo(
 () => templates.find((t) => String(t.id) === String(templateId)) || null,
 [templates, templateId]
 );

 const placeholderVars = useMemo(
 () => ({
 vendor_name: vp.business_name || 'Your business',
 vendor_address: vendorAddress || '—',
 client_name: clientName.trim() || 'Client',
 client_email: clientEmail.trim() || '—',
 service_description: serviceDescription.trim() || 'Services as agreed',
 event_date: eventDate
 ? new Date(eventDate + 'T12:00:00').toLocaleDateString(undefined, {
 year: 'numeric',
 month: 'long',
 day: 'numeric',
 })
 : 'To be confirmed',
 amount: amount ? `$${Number(amount).toFixed(2)}` : 'As invoiced',
 contract_date: new Date().toLocaleDateString(undefined, {
 year: 'numeric',
 month: 'long',
 day: 'numeric',
 }),
 }),
 [vp.business_name, vendorAddress, clientName, clientEmail, serviceDescription, eventDate, amount]
 );

 const applyTemplate = (tpl) => {
 if (!tpl) return;
 setTemplateId(tpl.id);
 setTitle(tpl.name);
 setBodyHtml(applyContractPlaceholders(tpl.body_html, placeholderVars));
 };

 const refreshPlaceholders = () => {
 const source = selectedTemplate?.body_html || bodyHtml;
 if (!source) return;
 setBodyHtml(applyContractPlaceholders(source, placeholderVars));
 };

 const createMutation = useMutation({
 mutationFn: (payload) => vendorContractApi.create(payload),
 onSuccess: (res) => {
 success('Contract created', 'You can send a signing link or email next.');
 queryClient.invalidateQueries({ queryKey: ['vendor-contracts'] });
 const linkedId = res?.data?.booking_id || linkedBookingIdRef.current || bookingIdParam;
 if (linkedId) {
 queryClient.invalidateQueries({ queryKey: ['vendorBooking', linkedId] });
 queryClient.invalidateQueries({ queryKey: ['booking', linkedId] });
 queryClient.invalidateQueries({ queryKey: ['vendorBookings'] });
 queryClient.invalidateQueries({ queryKey: ['bookings'] });
 }
 navigate(`/vendor/contracts/${res?.data?.id}`);
 },
 onError: (err) => {
 showError('Create failed', err?.response?.data?.message || 'Could not create contract.');
 },
 });

 const updateMutation = useMutation({
 mutationFn: (payload) => vendorContractApi.update(editId, payload),
 onSuccess: (res) => {
 success('Contract updated', 'Your changes have been saved.');
 queryClient.invalidateQueries({ queryKey: ['vendor-contracts'] });
 queryClient.invalidateQueries({ queryKey: ['vendor-contract', editId] });
 const linkedId = res?.data?.booking_id || linkedBookingIdRef.current || bookingIdParam;
 if (linkedId) {
 queryClient.invalidateQueries({ queryKey: ['vendorBooking', linkedId] });
 queryClient.invalidateQueries({ queryKey: ['booking', linkedId] });
 }
 navigate(`/vendor/contracts/${editId}`, { replace: true });
 },
 onError: (err) => {
 showError('Update failed', err?.response?.data?.message || 'Could not update contract.');
 },
 });

 const handleGenerateAi = async () => {
 if (!canUseAi || isGenerating) return;
 setAiError('');
 setIsGenerating(true);
 try {
 const prompt = [
 'Draft a professional service contract in clean HTML (use h2/h3/p/ul/li only).',
 `Provider business: ${placeholderVars.vendor_name}.`,
 `Client: ${placeholderVars.client_name} (${placeholderVars.client_email}).`,
 `Services: ${placeholderVars.service_description}.`,
 `Event/service date: ${placeholderVars.event_date}.`,
 `Amount: ${placeholderVars.amount}.`,
 aiPrompt.trim() ? `Additional instructions: ${aiPrompt.trim()}` : '',
 'Include sections for services, payment, cancellation, liability, and electronic signature acceptance.',
 'Do not wrap in markdown fences. Output HTML only.',
 ]
 .filter(Boolean)
 .join(' ');

 const response = await supportChatApi.sendMessage({ message: prompt, history: [] });
 const generated = normalizeChatReply(response?.data?.reply);
 if (!generated) {
 setAiError('Could not generate contract. Try again.');
 return;
 }
 let html = generated.trim();
 if (!html.includes('<')) {
 html = html
 .split(/\n\n+/)
 .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
 .join('');
 }
 setBodyHtml(html);
 if (!title.trim()) setTitle('Service Agreement');
 } catch (err) {
 setAiError(err?.response?.data?.message || 'Could not generate contract. Try again.');
 } finally {
 setIsGenerating(false);
 }
 };

 const bodyIsEmpty = !bodyHtml || bodyHtml.replace(/<[^>]*>/g, '').trim() === '';
 const saving = createMutation.isPending || updateMutation.isPending;
 const showingExistingVendorSig = Boolean(existingVendorSignature) && !clearVendorSignature && !vendorSignatureData;
 const hasVendorSignatureReady = Boolean(vendorSignatureData) || showingExistingVendorSig;

 const handleClearVendorSignature = () => {
 setExistingVendorSignature(null);
 setVendorSignatureData(null);
 setClearVendorSignature(true);
 setVendorSignPadKey((k) => k + 1);
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (!clientName.trim()) {
 showError('Missing client', 'Enter the client name.');
 return;
 }
 if (bodyIsEmpty) {
 showError('Missing content', 'Add contract content or pick a template.');
 return;
 }
 if (vendorSignatureData && !vendorSignerName.trim()) {
 showError('Missing name', 'Enter your name to include your signature.');
 return;
 }
 const linkedBookingId = linkedBookingIdRef.current || bookingIdParam || null;
 const payload = {
 title: title.trim() || 'Service Agreement',
 body_html: bodyHtml,
 client_name: clientName.trim(),
 client_email: clientEmail.trim() || null,
 client_phone: clientPhone.trim() || null,
 template_id: templateId || null,
 notes: notes.trim() || null,
 // Persist only document overrides — business logo is applied at display time.
 logo_url: logoUrl && logoUrl !== profileLogo ? logoUrl : null,
 status: isEdit ? undefined : 'draft',
 client_id: linkedClientId || null,
 booking_id: linkedBookingId || null,
 };

 if (vendorSignatureData) {
 payload.vendor_signature_data = vendorSignatureData;
 payload.vendor_signer_name = vendorSignerName.trim() || defaultVendorSigner || null;
 } else if (isEdit && clearVendorSignature) {
 payload.clear_vendor_signature = true;
 }

 if (isEdit) {
 updateMutation.mutate(payload);
 } else {
 createMutation.mutate({ ...payload, status: 'draft' });
 }
 };

 if (
 (copyFromId && copyLoading) ||
 (isEdit && editLoading) ||
 (bookingIdParam && !isEdit && !copyFromId && bookingPrefillLoading)
 ) {
 return (
 <div className="product-panel py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading contract…</p>
 </div>
 );
 }

 if (isEdit && editError) {
 return (
 <div className="product-panel py-16 text-center px-4">
 <p className="text-ink-secondary mb-4">Could not load contract.</p>
 <Link to="/vendor/contracts" className="text-ink hover:underline text-sm">
 Back to contracts
 </Link>
 </div>
 );
 }

 const pageTitle = isEdit
 ? 'Edit contract'
 : copyFromId
 ? 'Copy contract for another client'
 : 'New contract';

 const pageSubtitle = isEdit
 ? 'Update client details, logo, your signature, or contract terms, then save.'
 : copyFromId
 ? 'Content was copied from an existing contract. Enter the new client, optionally sign, then save.'
 : bookingIdParam
 ? 'Client details were filled from the booking. Add terms, sign as the vendor, then save for the client to sign.'
 : 'Pick a template, add your logo, fill client details, sign as the vendor, then save and send for the client’s signature.';

 return (
 <div className="space-y-5">
 <div className="max-w-5xl mx-auto space-y-4">
 <div>
 <Link
 to={isEdit ? `/vendor/contracts/${editId}` : '/vendor/contracts'}
 className="inline-flex items-center gap-2 text-sm text-ink-secondary hover:text-gray-900 mb-3"
 >
 <ArrowLeft className="w-4 h-4" />
 {isEdit ? 'Back to contract' : 'Back to contracts'}
 </Link>
 <VendorPageHeader title={pageTitle} subtitle={pageSubtitle} />
 </div>

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.contractCreate.title}
 steps={VENDOR_PAGE_GUIDES.contractCreate.steps}
 icon={ListChecks}
 />

 <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-4">
 <div className="lg:col-span-2 space-y-4">
 <div className="product-panel p-4 space-y-3">
 <h2 className="text-sm font-semibold text-ink">Contract logo</h2>
 <BrandImagePicker
 label="Logo on this contract"
 value={logoUrl}
 onChange={setLogoUrl}
 fallbackUrl={profileLogo}
 hint="Defaults to your business logo. Upload here only to override for this contract — it won’t change or re-save your business logo."
 aspectClass="aspect-[2/1] max-h-36"
 uploadImageType="document"
 showGallery={false}
 galleryTitle="Your business logo"
 presetImages={
 profileLogo
 ? [{ id: 'business-logo', image_url: profileLogo, label: 'Business logo' }]
 : []
 }
 />
 </div>

 <div className="product-panel p-4 space-y-3">
 <h2 className="text-sm font-semibold text-ink">Templates</h2>
 {templatesLoading ? (
 <Loader2 className="w-5 h-5 animate-spin text-ink" />
 ) : (
 <div className="space-y-2 max-h-72 overflow-y-auto">
 {templates.map((tpl) => (
 <button
 key={tpl.id}
 type="button"
 onClick={() => applyTemplate(tpl)}
 className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors ${ String(templateId) === String(tpl.id) ? 'border-gray-900 bg-surface-muted' : 'border-border hover:border-gray-400' }`}
 >
 <div className="text-sm font-medium text-ink">{tpl.name}</div>
 {tpl.description && (
 <div className="text-xs text-ink-tertiary mt-0.5 line-clamp-2">{tpl.description}</div>
 )}
 </button>
 ))}
 </div>
 )}
 </div>

 <div className="product-panel p-4 space-y-3">
 <div className="flex items-center justify-between gap-2">
 <h2 className="text-sm font-semibold text-ink">AI draft</h2>
 {!canUseAi && (
 <Link to="/vendor/plan" className="text-xs text-ink hover:underline">
 Upgrade
 </Link>
 )}
 </div>
 <textarea
 value={aiPrompt}
 onChange={(e) => setAiPrompt(e.target.value)}
 rows={3}
 disabled={!canUseAi}
 placeholder="e.g. Include a 50% deposit clause and weekend overtime rates"
 className="w-full rounded-xl border border-border bg-surface px-3 py-1.5 text-xs text-ink disabled:bg-surface-muted disabled:text-gray-400"
 />
 {canUseAi ? (
 <button
 type="button"
 onClick={handleGenerateAi}
 disabled={isGenerating}
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-ink text-white text-sm font-medium hover:bg-black disabled:opacity-60"
 >
 {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
 {isGenerating ? 'Generating…' : 'Generate with AI'}
 </button>
 ) : (
 <span
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-ink/40 text-white text-sm font-medium cursor-not-allowed"
 title="Upgrade to use AI"
 >
 <Bot className="w-4 h-4" />
 Generate with AI
 </span>
 )}
 {aiError && <p className="text-xs text-red-600">{aiError}</p>}
 {!canUseAi && (
 <p className="text-xs text-ink-tertiary">AI drafting is available on paid plans.</p>
 )}
 </div>
 </div>

 <div className="lg:col-span-3 space-y-4">
 <div className="product-panel p-4 space-y-4">
 <Input
 label="Contract title"
 value={title}
 onChange={(e) => setTitle(e.target.value)}
 placeholder="Service Agreement"
 />
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 <Input
 label="Client name *"
 value={clientName}
 onChange={(e) => setClientName(e.target.value)}
 required
 />
 <Input
 label="Client email"
 type="email"
 value={clientEmail}
 onChange={(e) => setClientEmail(e.target.value)}
 placeholder="For sending the signing link"
 />
 <Input
 label="Client phone"
 value={clientPhone}
 onChange={(e) => setClientPhone(e.target.value)}
 />
 <DatePicker
 label="Event / service date"
 value={eventDate}
 onChange={(v) => setEventDate(v || '')}
 placeholder="Select date"
 allowPastDates
 />
 <Input
 label="Amount (optional)"
 type="number"
 min="0"
 step="0.01"
 value={amount}
 onChange={(e) => setAmount(e.target.value)}
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-1">Service description</label>
 <textarea
 value={serviceDescription}
 onChange={(e) => setServiceDescription(e.target.value)}
 rows={2}
 className="w-full rounded-xl border border-border bg-surface px-3 py-1.5 text-xs text-ink"
 placeholder="Photo booth package for wedding reception"
 />
 </div>
 <button
 type="button"
 onClick={refreshPlaceholders}
 className="text-xs font-medium text-ink-secondary hover:text-gray-900 underline"
 >
 Re-apply template placeholders with current details
 </button>
 <div>
 <div className="flex items-center justify-between gap-2 mb-1">
 <label className="block text-sm font-medium text-ink-secondary">Contract body</label>
 <button
 type="button"
 onClick={() => setPreviewOpen(true)}
 disabled={bodyIsEmpty}
 className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-secondary hover:text-gray-900 disabled:opacity-40"
 >
 <Eye className="w-3.5 h-3.5" />
 Preview
 </button>
 </div>
 <ContractRichTextEditor
 value={bodyHtml}
 onChange={setBodyHtml}
 placeholder="Select a template, generate with AI, or write your terms…"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-1">Internal notes (optional)</label>
 <textarea
 value={notes}
 onChange={(e) => setNotes(e.target.value)}
 rows={2}
 className="w-full rounded-xl border border-border bg-surface px-3 py-1.5 text-xs text-ink"
 />
 </div>
 </div>

 <div className="product-panel p-4 space-y-3">
 <div>
 <h2 className="text-sm font-semibold text-ink">Your signature (vendor)</h2>
 <p className="text-xs text-ink-tertiary mt-1">
 Sign here so the client sees your signature when they open the contract. The client still signs
 separately via the signing link.
 </p>
 </div>
 <Input
 label="Your full legal name"
 value={vendorSignerName}
 onChange={(e) => setVendorSignerName(e.target.value)}
 placeholder="Name as it should appear on the contract"
 />
 {showingExistingVendorSig ? (
 <div className="space-y-2">
 <p className="text-xs font-medium text-emerald-700">Signature on file</p>
 <img
 src={existingVendorSignature}
 alt="Your signature"
 className="max-w-xs border border-border rounded-xl bg-surface"
 />
 <button
 type="button"
 onClick={handleClearVendorSignature}
 className="text-xs font-medium text-ink-secondary hover:text-gray-900 underline"
 >
 Clear and re-sign
 </button>
 </div>
 ) : (
 <SignaturePad key={vendorSignPadKey} onChange={setVendorSignatureData} />
 )}
 {hasVendorSignatureReady ? (
 <p className="text-xs text-emerald-700">Your signature will be included when you save.</p>
 ) : (
 <p className="text-xs text-ink-tertiary">Optional — you can save without signing and add it later.</p>
 )}
 </div>

 <div className="flex flex-wrap gap-3">
 <Button type="submit" loading={saving} className="rounded-md">
 {isEdit ? 'Save changes' : 'Save contract'}
 </Button>
 <Button
 type="button"
 variant="outline"
 onClick={() => setPreviewOpen(true)}
 disabled={bodyIsEmpty}
 icon={Eye}
 className="rounded-md"
 >
 Preview
 </Button>
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate(isEdit ? `/vendor/contracts/${editId}` : '/vendor/contracts')}
 className="rounded-md"
 >
 Cancel
 </Button>
 </div>
 </div>
 </form>
 </div>

 <ModalShell
 isOpen={previewOpen}
 onClose={() => setPreviewOpen(false)}
 labelledBy="contract-preview-title"
 className="items-end sm:items-center p-0 sm:p-4"
 panelClassName="max-w-3xl"
 >
 <div className="bg-surface w-full sm:rounded-lg shadow-sm max-h-[92vh] flex flex-col border border-border min-w-0 overflow-hidden">
 <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-border shrink-0">
 <div className="min-w-0">
 <h2 id="contract-preview-title" className="text-lg font-semibold text-ink">
 Contract preview
 </h2>
 <p className="text-xs text-ink-tertiary mt-0.5 truncate">
 {title || 'Untitled contract'}
 </p>
 </div>
 <button
 type="button"
 onClick={() => setPreviewOpen(false)}
 className="p-2 rounded-xl text-ink-tertiary hover:bg-surface-muted shrink-0"
 aria-label="Close preview"
 >
 <X className="w-5 h-5" />
 </button>
 </div>
 <div className="overflow-y-auto overflow-x-hidden p-4 sm:p-8 flex-1 min-w-0">
 {(logoUrl || profileLogo) && (
 <img
 src={logoUrl || profileLogo}
 alt=""
 className="h-14 w-auto max-w-[180px] object-contain mb-4 rounded-md border border-border"
 />
 )}
 <p className="text-xs font-semibold uppercase tracking-wider text-ink-tertiary mb-1">
 {vp.business_name || 'Your business'}
 </p>
 <h3 className="text-xl font-bold text-ink mb-4">
 {title || 'Untitled contract'}
 </h3>
 <ContractBodyHtml
 html={bodyHtml || '<p class="text-gray-400">Nothing to preview yet.</p>'}
 className="text-ink"
 />
 {(vendorSignatureData || showingExistingVendorSig) && (
 <div className="mt-8 pt-6 border-t border-border">
 <p className="text-xs font-semibold uppercase text-ink-tertiary mb-2">
 Vendor signature
 </p>
 <p className="text-sm text-ink-secondary mb-2">
 Signed by <strong>{vendorSignerName || defaultVendorSigner || 'Vendor'}</strong>
 </p>
 <img
 src={vendorSignatureData || existingVendorSignature}
 alt="Vendor signature"
 className="max-w-xs border border-border rounded-xl"
 />
 </div>
 )}
 </div>
 <div className="px-4 sm:px-6 py-3 border-t border-border flex justify-end shrink-0">
 <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-md">
 Close
 </Button>
 </div>
 </div>
 </ModalShell>
 </div>
 );
};

export default VendorContractCreate;
